import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { logo } from "../assets/svg/svg";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "universal-cookie";
import { useLocation } from "react-router-dom";
import { DeleteOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { updateInsecureMail } from "../service/api-actions";
import { CompleteLoader } from "../modals/common-alert";
import { deleteEmails, permanentlyDeleteEmail, updateEmailAttributeValue, updateAttribute } from ".././helper/emailHelper";
import { ArchiveRestore, Archive, FolderInput, FolderOutput, MailOpen, Mail, ArrowDownUp } from "lucide-react";
import { Tooltip } from "antd";
import { toast } from "react-toastify";
import { markMessageAsImportant } from "../helper/web3helper";
import { getHostContract } from "../helper/contract-helper";
import { folders } from "../assets/svg/folder-svg.js";
import '../components/styles/messagebox.css'
import { getWeb2emails } from "../helper/dataFormatHelper.js";
import { removeMessageFromIndexedDb } from "../db/indexed-helper.js";
import { reportError, toErrorString } from "../utils/errorLogger.js";
import { notifyChainFailure } from "../utils/txFeedback.js";
import AnchoredMenu from "./AnchoredMenu.js";
import { applyAdvancedFilter, applySearch, flattenMessages, SORT_OPTIONS, DEFAULT_SORT } from "../helper/messageListHelper.js";

/** The select-all caret menu. Mirrors the mobile client's list, "None" included. */
const SELECTION_MODES = [
  { value: 1, label: "All" },
  { value: null, label: "None" },
  { value: 2, label: "Read" },
  { value: 3, label: "Unread" },
  { value: 4, label: "Starred" },
];

const isRead = (message) => !!(message?.isRead || message?.read);

const MessageBoxHeader = (props) => {
  const cookies = new Cookies();
  const getFolderIndex = useSelector((state) => state.folderIndex);
  const foldersValue = useSelector((state) => state.foldersList)
  const [user] = useState(cookies.get("userObject"));
  const { pageType } = props;
  const [isCheckOpen, setIsCheckOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [folderList, setFolderList] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 767);
  const dispatch = useDispatch();
  const selectedValue = useSelector((state) => state.selectedType);
  const selectedMessageList = useSelector((state) => state.selectedMessageList);
  const currentPage = useSelector((state) => state.currentPage);
  const messagesPerPage = useSelector((state) => state.messagesPerPage);
  const searchTerm = useSelector((state) => state.searchTerm);
  const getFilters = useSelector((state) => state.filterObject);
  const sortType = useSelector((state) => state.sortType) || DEFAULT_SORT;
  const filteredMessage = useSelector((state) => state.selectedMessageList);
  const [loader, setloader] = useState(false);
  const [text, setText] = useState("")
  const pagiClickRight = useSelector((state) => state.pagiClickRight);
  const pagiClickLeft = useSelector((state) => state.pagiClickLeft);
  const checkboxRef = useRef(null);
  const dropdownRef = useRef(null);
  const folderTriggerRef = useRef(null);
  const sortRef = useRef(null);
  const settingRef = useRef(null);
  const type = props.pageType === "Insecure" ? "Web2 Inbox" : props.pageType;
  const handleOptionChange = props.handleOptionChange;
  const selectedOption = props.selectedOption;
  const location = useLocation();
  const [settings, setSettings] = useState(false)
  const isVisibleFilter = !["Draft", "Outbox"].includes(props.pageType);
  const userName = localStorage.getItem('UserName');

  // What the list component is actually showing, after grouping / show-filter / search / sort.
  // The toolbar used to read props.messageList here — the raw, unfiltered "All" collection — so
  // select-all built its id set from messages that were not on screen.
  const renderList = useMemo(() => props.renderList || [], [props.renderList]);
  const messageList = useMemo(() => props.messageList || [], [props.messageList]);
  const readList = useMemo(() => props.readList || [], [props.readList]);
  const unreadList = useMemo(() => props.unreadList || [], [props.unreadList]);

  const clearSelection = useCallback(() => {
    dispatch({ type: "SELECTED_MESSAGE_LIST", payload: [] });
    dispatch({ type: "SELECT_TYPE", payload: null });
  }, [dispatch]);

  // Selection is scoped to one mailbox. Without this it survived navigation: the bulk icons rendered
  // on a page where nothing looked ticked, and clicking one threw on a message id that does not exist
  // in the new list — before Swal could even open, so the button appeared completely dead.
  useEffect(() => {
    dispatch({ type: "MESSAGE_PER_PAGE", payload: 50 });
    dispatch({ type: "CURRENT_PAGE", payload: 1 });
    dispatch({ type: "SELECTED_MESSAGE_LIST", payload: [] });
    dispatch({ type: "SELECT_TYPE", payload: null });
  }, [location.pathname, getFolderIndex, dispatch]);

  // Narrowing the list must also drop the selection, or a bulk action reaches mail the user cannot see.
  // Select all 50 on "All", switch Show to "Unread" (or type a search), press Archive: the ids stayed in
  // redux, so every originally-selected message was archived — including the ones now filtered out. The
  // rows even visibly unticked (Messages.js re-syncs the ticks to what is on screen) while redux still
  // held them, so the user saw an empty selection and archived 50 messages.
  //
  // Page is reset with it because a narrowed list may not have the page you were on. `messagesPerPage` is
  // deliberately NOT reset here — that is the user's own choice and only the route change should clear it.
  useEffect(() => {
    dispatch({ type: "CURRENT_PAGE", payload: 1 });
    dispatch({ type: "SELECTED_MESSAGE_LIST", payload: [] });
    dispatch({ type: "SELECT_TYPE", payload: null });
  }, [selectedOption, searchTerm, getFilters, sortType, dispatch]);

  // Turning the page must drop the selection too, for the same reason — and this one was silent data
  // loss. Nothing cleared the selection when CURRENT_PAGE changed, and toggleSelectAllVisible UNIONED
  // the new page into whatever was already held, so selections accumulated across pages. Select all on
  // page 1, go to page 2, press Archive: 50 messages the user could not see were archived. The rows on
  // page 2 correctly showed nothing ticked (Messages.js re-syncs the ticks to the visible page), so the
  // toolbar was acting on a selection the UI was telling the user did not exist.
  //
  // `messagesPerPage` is a dependency because it shrinks the page without changing CURRENT_PAGE: going
  // 50 -> 10 per page while on page 1 would otherwise leave 40 selected ids off-screen and still armed.
  useEffect(() => {
    clearSelection();
  }, [currentPage, messagesPerPage, clearSelection]);

  const handleResize = () => {
    setIsMobileView(window.innerWidth <= 767);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ---------------------------------------------------------------- selection

  /** The messages on the current page, group children included. */
  const visibleMessages = useMemo(() => {
    const start = (currentPage - 1) * messagesPerPage;
    return flattenMessages(renderList.slice(start, start + messagesPerPage));
  }, [renderList, currentPage, messagesPerPage]);

  const visibleIds = useMemo(
    () => visibleMessages.map((item) => item?.id).filter((id) => id !== undefined && id !== null),
    [visibleMessages]
  );

  const areAllVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedMessageList.includes(id));

  // Replaces, never unions. The union was how a selection grew past what the user could see: each page's
  // ids were added to the previous page's and nothing ever took them out again. The selection is now
  // exactly the ticked rows of the page in front of the user, which is the only set the row checkboxes
  // are capable of showing.
  const toggleSelectAllVisible = () => {
    if (areAllVisibleSelected) {
      dispatch({ type: "SELECTED_MESSAGE_LIST", payload: [] });
      dispatch({ type: "SELECT_TYPE", payload: null });
    } else {
      dispatch({ type: "SELECTED_MESSAGE_LIST", payload: visibleIds });
      dispatch({ type: "SELECT_TYPE", payload: 1 });
    }
  };

  /**
   * The caret menu selects directly rather than parking a mode in redux and hoping the list component
   * reacts to it. Re-picking the mode you are already on used to be a no-op, because the reducer value
   * never changed and the effect that did the work never re-ran.
   */
  const applySelectionMode = (mode) => {
    let picked;
    switch (mode) {
      case 1: picked = visibleMessages; break;
      case 2: picked = visibleMessages.filter((item) => isRead(item)); break;
      case 3: picked = visibleMessages.filter((item) => !isRead(item)); break;
      case 4: picked = visibleMessages.filter((item) => item?.isStarred); break;
      default: picked = []; break;
    }
    const ids = picked.map((item) => item?.id).filter((id) => id !== undefined && id !== null);
    dispatch({ type: "SELECT_TYPE", payload: mode ?? null });
    dispatch({ type: "SELECTED_MESSAGE_LIST", payload: ids });
    setIsCheckOpen(false);
  };

  /**
   * Selected ids -> message objects, with the ones that no longer exist dropped.
   *
   * Resolved against the CURRENT PAGE only. Two earlier narrowings landed here: first a fallback pass over
   * `messageList` (the raw, unfiltered collection) was removed, because an id that is no longer on screen
   * is exactly the one that must NOT resolve — with the fallback in place a stale selection resolved
   * straight back to the full mailbox and bulk actions acted on it. That left `renderList`, which is still
   * every page of the filtered list, so a selection carried over from another page resolved happily and
   * got archived/deleted unseen.
   *
   * Clearing the selection on page change (above) already stops ids from another page ever being held, so
   * this is the structural backstop rather than the only defence: it means "a bulk action can only touch
   * rows the user is looking at" is true by construction, not by effect-ordering luck.
   */
  const messageIndex = useMemo(() => {
    const map = new Map();
    visibleMessages.forEach((item) => { if (item?.id !== undefined) map.set(item.id, item); });
    return map;
  }, [visibleMessages]);

  /**
   * How many messages a bulk action would actually touch — resolved exactly the way requireSelection
   * resolves them, so the number in the toolbar and in the confirm dialog is the number that changes.
   */
  const selectionCount = useMemo(
    () => (filteredMessage || []).filter((id) => messageIndex.has(id)).length,
    [filteredMessage, messageIndex]
  );

  /** "1 email" / "12 emails", so no confirm can ask a vague question about an unknown amount of mail. */
  const countLabel = (count) => `${count} ${count === 1 ? "email" : "emails"}`;

  /**
   * Every bulk handler used to do `filteredMessage.map(id => messageList.find(...))` and then read
   * `.mailtype` off the result — which is `undefined` for any id that has gone (deleted, archived,
   * moved, or left behind by a route change). That threw a TypeError before the confirm dialog, so
   * the button did nothing at all and said nothing.
   */
  const requireSelection = () => {
    const selected = filteredMessage.map((id) => messageIndex.get(id)).filter(Boolean);
    if (selected.length === 0) {
      toast.info("Select at least one email first.");
      clearSelection();
      return null;
    }
    return selected;
  };

  // ------------------------------------------------------------------ counts

  /** Counts follow the active search and advanced filter, so the labels match what a click produces. */
  const visibleCount = useCallback(
    (list) => applySearch(applyAdvancedFilter(list, getFilters), searchTerm).length,
    [getFilters, searchTerm]
  );
  const allCount = useMemo(() => visibleCount(messageList), [visibleCount, messageList]);
  const readCount = useMemo(() => visibleCount(readList), [visibleCount, readList]);
  const unreadCount = useMemo(() => visibleCount(unreadList), [visibleCount, unreadList]);
  const starredCount = useMemo(
    () => visibleCount(messageList.filter((message) => message?.isStarred)),
    [visibleCount, messageList]
  );

  /** The Show filter, kept in step with the mobile client's FILTER_LABELS. */
  const showOptions = useMemo(() => ([
    { value: 'all', label: 'All', count: allCount, show: true },
    { value: 'read', label: 'Read', count: readCount, show: pageType !== "Sent" },
    { value: 'unread', label: 'Unread', count: unreadCount, show: pageType !== "Sent" },
    { value: 'starred', label: 'Starred', count: starredCount, show: true },
  ].filter((option) => option.show)), [allCount, readCount, unreadCount, starredCount, pageType]);

  const activeShowOption = showOptions.find((option) => option.value === selectedOption) || showOptions[0];

  const totalLength = renderList.length;
  const rangeStart = totalLength === 0 ? 0 : currentPage * messagesPerPage - messagesPerPage + 1;
  const rangeEnd = Math.min(currentPage * messagesPerPage, totalLength);

  const handleDropdownSelect = (value) => () => {
    setIsDropdownOpen(false);
    handleOptionChange({ target: { value } });
  };

  // ----------------------------------------------------------- bulk handlers

  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  async function bulkWeb2Emails(Emails, batchSize, type, resValue, functionName) {
    if (Emails.length > 0) {
      const chunks = chunkArray(Emails, batchSize);
      for (const chunk of chunks) {
        let id = chunk;
        const value = resValue;
        await functionName(type, id, value);
      }
    }
  }
  /** Returns false if any chunk's chain write failed to land. */
  async function bulkEmailsRestore(trash, type, functionName) {
    let ok = true;
    if (trash.length > 0) {
      const chunks = chunkArray(trash, 25);
      for (const chunk of chunks) {
        let value = chunk;
        if (!await functionName(value, null, null, null, type, null)) ok = false;
      }
    }
    return ok;
  }

  /** Returns false if any chunk's chain write failed to land. */
  async function deleteBulkEmails(inbox, __, value3, pagetype, getFolderIndex, functionName) {
    let ok = true;
    if (value3.length > 0) {
      const updatedResponse = [];
      value3.forEach((data) => {
        updatedResponse.push(data.messageId);
      });
      const chunks = chunkArray(updatedResponse, 25);
      for (const chunk of chunks) {
        let value = chunk;
        if (!await functionName(inbox, value, null, value3, pagetype, getFolderIndex)) ok = false;
      }
    }
    return ok;
  }
  async function web2EmailsDelete(Emails, batchSize, type, functionName, resValue) {
    if (Emails.length > 0) {
      const updatedResponse = [];
      Emails.forEach((data) => {
        updatedResponse.push({
          id: data.messageId || data.id,
          value: resValue,
        });
      });
      const chunks = chunkArray(updatedResponse, batchSize);
      for (const chunk of chunks) {
        let value = chunk;
        await functionName(type, "", value);
      }
    }
  }
  const bulkDelete = async () => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    const web3 = selectedMessages.filter((data) => data.mailtype === "web3emails");
    const senttrash = selectedMessages.filter((data) => data.mailtype === "Senttrash");
    const web2 = selectedMessages.filter((data) => data.mailtype === "web2emails");
    Swal.fire({
      text: `Are you sure you want to delete ${countLabel(selectedMessages.length)}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1677ff",
      cancelButtonColor: "#7f7f7f",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: "compose-sweet-alert",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setloader(true);
        setText("Please wait, your data is being deleted, Going forward it will be fast.")
        try {
          const ok1 = await deleteBulkEmails(null, null, web3, props.pageType, null, deleteEmails)
          const ok2 = await deleteBulkEmails(null, null, senttrash, "Sent", null, deleteEmails)
          await web2EmailsDelete(web2, 50, "bulkdelete", updateInsecureMail, 1)
          if (!ok1 || !ok2) notifyChainFailure('delete the selected emails');
        }
        catch (err) {
          const errorStr = toErrorString(err || err.message);
          reportError({
            endpoint: '-',
            message: `FileName: MessageBoxHeader.js, Fun: bulkDelete, ${String(err.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
          });
          console.error(err)
        }
        finally {
          clearSelection();
          props.reRender();
          setloader(false);
          setText("")
        }
      }
    });
  };


  const handleRestore = async () => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    const web3 = selectedMessages.filter((data) => data.mailtype === "web3emails");
    const senttrash = selectedMessages.filter((data) => data.emailType === "Senttrash");
    const web2 = selectedMessages.filter((data) => data.mailtype === "web2emails");
    Swal.fire({
      text: `Are you sure you want to restore ${countLabel(selectedMessages.length)}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1677ff",
      cancelButtonColor: "#7f7f7f",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: "compose-sweet-alert",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setloader(true);
        setText("Please wait, your data is being restored, Going forward it will be fast.")
        try {
          const ok1 = await bulkEmailsRestore(web3, "inboxtrash", deleteEmails)
          const ok2 = await bulkEmailsRestore(senttrash, "Senttrash", deleteEmails)
          await web2EmailsDelete(web2, 50, "bulkdelete", updateInsecureMail, 2)
          if (!ok1 || !ok2) notifyChainFailure('restore the selected emails');
        }
        catch (err) {
          const errorStr = toErrorString(err || err.message);
          reportError({
            endpoint: '-',
            message: `FileName: MessageBoxHeader.js, Fun: handleRestore, ${String(err.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
          });
          console.error(err)
        }
        finally {
          clearSelection();
          props.reRender();
          setloader(false);
          setText("")
        }
      }
    });
  };
  const permanentDelete = async () => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    const web3 = selectedMessages.filter((data) => data.mailtype === "web3emails");
    const senttrash = selectedMessages.filter((data) => data.emailType === "Senttrash");
    const web2 = selectedMessages.filter((data) => data.mailtype === "web2emails");

    Swal.fire({
      // Says "permanently": this is the Trash page's delete, and it is the one action here nothing can undo.
      text: `Are you sure you want to permanently delete ${countLabel(selectedMessages.length)}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1677ff",
      cancelButtonColor: "#7f7f7f",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: "compose-sweet-alert",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setloader(true);
        setText("Please wait, your data is being deleted, Going forward it will be fast.")
        try {
          let ok = await deleteBulkEmails(senttrash, null, senttrash, "Trash", null, deleteEmails)
          if (web3.length > 0) {
            const updatedResponse = [];
            web3.forEach((data) => {
              updatedResponse.push(data.messageId);
            });
            const chunks = chunkArray(updatedResponse, 25);
            for (const chunk of chunks) {
              const hash = await permanentlyDeleteEmail(
                user.name,
                chunk,
                'hostContract'
              )
              if (!hash) ok = false;
            }
          }
          await web2EmailsDelete(web2, 50, "bulkdelete", updateInsecureMail, 3)
          if (!ok) notifyChainFailure('permanently delete the selected emails');
        }
        catch (Err) {
          const errorStr = toErrorString(Err || Err.message);
          reportError({
            endpoint: '/updateInsecureMail',
            message: `FileName: MessageBoxHeader.js, Fun: permanentDelete, ${String(Err.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
          });
          console.error(Err)
        }
        finally {
          clearSelection();
          props.reRender();
          setloader(false);
          setText("")
        }

      }
    });
  };

  const handleOptionClick = async (data) => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) { setFolderList(false); return; }
    const targetType = pageType === 'Sent' ? `sent-folder-${parseInt(data.id)}` : `inbox-folder-${parseInt(data.id)}`;
    setFolderList(false);
    setloader(true);
    setText("Please wait, your changes are being stored on the blockchain, Going forward it will be fast.")
    try {
      if (pageType === "Insecure") {
        const formattedresponse = await getWeb2emails(user.name)
        // Matched against the RESOLVED selection, not the raw redux id list. getWeb2emails re-reads the
        // whole web2 mailbox, so testing it against unresolved ids was the one path here that could still
        // reach a message that is not on the page in front of the user.
        const selectedIds = new Set(selectedMessages.map((mail) => mail.id));
        const result = formattedresponse.map((mail) => selectedIds.has(mail.id) && mail.physmessage_id).filter(Boolean);
        await updateAttribute(user.name, result, targetType, 'folder', true);
      } else {
        const ids = selectedMessages.map((mail) => mail.id).filter((id) => id !== undefined && id !== null);
        const ok = await updateEmailAttributeValue(user.name, ids, [{ key: 'folder', value: (data.id).toString() }], 'hostContract');
        if (!ok) notifyChainFailure(`move ${ids.length > 1 ? 'these emails' : 'this email'} to "${data.name || 'that folder'}"`);
      }
    } catch (err) {
      const errorStr = toErrorString(err || err.message);
      reportError({
        endpoint: '-',
        message: `FileName: MessageBoxHeader.js, Fun: handleOptionClick, ${String(err.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      console.error(err);
    } finally {
      clearSelection();
      props.reRender();
      setloader(false);
      setText("")
    }
  };
  const folderMailDelete = async () => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    const sent = selectedMessages.filter((data) => data.emailType === "sent");
    const inbox = selectedMessages.filter((data) => data.emailType === "inbox");
    const web2 = selectedMessages.filter((data) => data.mailtype === "web2emails");

    Swal.fire({
      text: `Are you sure you want to delete ${countLabel(selectedMessages.length)}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1677ff",
      cancelButtonColor: "#7f7f7f",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: "compose-sweet-alert",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setloader(true);
        setText("Please wait, your email is being deleted, Going forward it will be fast.")
        try {
          const ok1 = await deleteBulkEmails(sent, null, sent, "Folders", getFolderIndex, deleteEmails);
          const ok2 = await deleteBulkEmails(inbox, null, inbox, "Folders", getFolderIndex, deleteEmails);
          await web2EmailsDelete(web2, 50, "bulkdelete", updateInsecureMail, 1)
          if (!ok1 || !ok2) notifyChainFailure('delete the selected emails');
        }
        catch (Err) {
          const errorStr = toErrorString(Err || Err.message);
          reportError({
            endpoint: '/updateInsecureMail',
            message: `FileName: MessageBoxHeader.js, Fun: folderMailDelete, ${String(Err.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
          });
          console.error(Err)
        }
        finally {
          clearSelection();
          props.reRender();
          setloader(false);
          setText("")
        }
      }
    });
  };
  const bulkRestoreArchive = async (resValue) => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    // Archive / "Move to inbox" was the one bulk action that fired straight off the icon: no confirm, no
    // count, nothing to read before a whole page of mail left the mailbox. Every other bulk action here
    // asks first, in this exact Swal, so this one now does too.
    const confirmation = await Swal.fire({
      text: resValue
        ? `Are you sure you want to archive ${countLabel(selectedMessages.length)}?`
        : `Are you sure you want to move ${countLabel(selectedMessages.length)} to the inbox?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1677ff",
      cancelButtonColor: "#7f7f7f",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: "compose-sweet-alert",
    });
    if (!confirmation.isConfirmed) return;
    setloader(true);
    setText("Please wait, your changes are being stored on the blockchain, Going forward it will be fast.")
    const web3emails = selectedMessages.filter((data) => ["Senttrash", "web3emails"].includes(data.mailtype)).map((data) => data.id);
    const web2 = selectedMessages.map((data) => data.mailtype === "web2emails" ? data.id : null).filter(data => data !== null);

    try {
      let ok = true;
      const chunks = chunkArray(web3emails, 25);
      for (const chunk of chunks) {
        if (!await updateEmailAttributeValue(user.name, chunk, [{ key: 'isArchive', value: (resValue).toString() }], 'hostContract')) ok = false;
      }
      await bulkWeb2Emails(web2, 50, "Web2emails", resValue, updateInsecureMail)
      if (!ok) notifyChainFailure(resValue ? 'archive the selected emails' : 'move the selected emails to the inbox');
    } catch (err) {
      const errorStr = toErrorString(err || err.message);
      reportError({
        endpoint: '/updateInsecureMail',
        message: `FileName: MessageBoxHeader.js, Fun: bulkRestoreArchive, ${String(err.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      toast.error("Failed to move Archive");
      console.log(err);
    } finally {
      clearSelection();
      props.reRender();
      setloader(false);
      setText("")
    }
  };
  /** Returns false if any chunk's chain write failed to land. */
  async function bulkStarred(inbox, contractMethods, response, functionName) {
    let ok = true;
    if (inbox.length > 0) {
      const chunks = chunkArray(inbox, 25);
      for (const chunk of chunks) {
        let obj = chunk;
        const value = response;
        const hash = await functionName(contractMethods, user.name, obj, value, user.wallet);
        if (!hash) ok = false;
      }
    }
    return ok;
  }
  const handlestared = async () => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    setloader(true);
    setText("Please wait, your changes are being stored on the blockchain, Going forward it will be fast.")
    const contractMethods = getHostContract();
    const inbox = selectedMessages.map((data) => data.mailtype === "web3emails" ? (data.id) : null).filter(data => data !== null);
    const web2 = selectedMessages.map((data) => data.mailtype === "web2emails" ? data.id : null).filter(data => data !== null);
    const response = selectedMessages.some((data) => data.isStarred === true);
    try {
      const ok = await bulkStarred(inbox, contractMethods, response, markMessageAsImportant)
      await bulkWeb2Emails(web2, 50, "fav", "", updateInsecureMail)
      if (!ok) notifyChainFailure(response ? 'unstar the selected emails' : 'star the selected emails');
    } catch (err) {
      const errorStr = toErrorString(err || err.message);
      reportError({
        endpoint: '/updateInsecureMail',
        message: `FileName: MessageBoxHeader.js, Fun: handlestared, ${String(err.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      console.error(err);
    } finally {
      clearSelection();
      props.reRender();
      setloader(false);
      setText("")
    }
  };
  function getColorFromJson(jsonString) {
    try {
      const json = jsonString;
      const color = json.folderProperties.find(value => value.key === 'color')['value']
      return color;
    } catch (error) {
      return "green";
    }
  }

  /**
   * Mark read / unread. This now requires a selection.
   *
   * It used to fall back to `messageList` — the entire mailbox, every page — when nothing was ticked,
   * and the menu item was not gated on selection, so one click on "Mark as read" fired chunked chain
   * writes across every message the user owned.
   */
  async function handleReadMessage(readType) {
    const result = requireSelection();
    if (!result) return;
    try {
      setloader(true);
      let chainOk = true;
      if (readType === "unread") {
        const inbox = result.map((data) => data.mailtype === "web3emails" && isRead(data) ? (data.id) : null).filter(data => data !== null);
        const web2 = result.map((data) => data.mailtype === "web2emails" && isRead(data) ? data.id : null).filter(data => data !== null);
        if (inbox.length > 0) {
          const chunks = chunkArray(inbox, 25);
          for (const chunk of chunks) {
            const ok = await updateEmailAttributeValue(user.name, chunk, [{ key: "isRead", value: "false" }], 'hostContract');
            if (!ok) chainOk = false;
            // remove the chunk ids from the index db
            for (const id of chunk) {
              await removeMessageFromIndexedDb(id, props?.pageType?.toLowerCase());
            }
          }
        }

        if (web2.length > 0) {
          await updateInsecureMail('isRead', web2, 0);
          for (const id of web2) {
            await removeMessageFromIndexedDb(id, props?.pageType?.toLowerCase());
          }
        }
      }
      else if (readType === "read") {
        const inbox = result.map((data) => data.mailtype === "web3emails" && !isRead(data) ? (data.id) : null).filter(data => data !== null);
        const web2 = result.map((data) => data.mailtype === "web2emails" && !isRead(data) ? data.id : null).filter(data => data !== null);

        if (inbox.length > 0) {
          const chunks = chunkArray(inbox, 25);
          for (const chunk of chunks) {
            const ok = await updateEmailAttributeValue(user.name, chunk, [{ key: "isRead", value: "true" }], 'hostContract');
            if (!ok) chainOk = false;
          }
        }
        if (web2.length > 0) {
          await updateInsecureMail('isRead', web2, 1);
        }
      }
      if (!chainOk) notifyChainFailure(`mark the selected emails as ${readType}`);
    }
    catch (err) {
      const errorStr = toErrorString(err || err.message);
      reportError({
        endpoint: '/updateInsecureMail',
        message: `FileName: MessageBoxHeader.js, Fun: handleReadMessage, ${String(err.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      console.error(err);
    }
    finally {
      clearSelection();
      props.reRender()
      setloader(false);
    }
  }

  /**
   * Folders page -> "Move to inbox".
   *
   * The old version branched on `selectedMessages.mailtype` — a property read off an ARRAY, so always
   * undefined and the branch dead — and its `senttrash` arm only assigned an unused local, issuing no
   * write at all while still calling reRender(). Sent mail therefore never left the folder, and the
   * button looked like it had worked. Folder membership is one `folder` attribute for both inbox and
   * sent records (see Folders.js, which matches both against the same folder id), so clearing it is
   * the same call for both.
   */
  const handleMoveFolders = async () => {
    const selectedMessages = requireSelection();
    if (!selectedMessages) return;
    const folderId = localStorage.getItem("selectedFolderId");
    setloader(true);
    setText("Please wait, your changes are being stored on the blockchain, Going forward it will be fast.")
    try {
      const web2 = selectedMessages.filter((data) => data.mailtype === "web2emails");
      const chainIds = selectedMessages
        .filter((data) => data.mailtype !== "web2emails")
        .map((data) => data.id)
        .filter((id) => id !== undefined && id !== null);

      if (web2.length > 0) {
        const result = web2.map((data) => data.physmessage_id).filter(Boolean);
        if (result.length > 0) await updateAttribute(user.name, result, `inbox-folder-${folderId}`, 'folder', false);
      }
      if (chainIds.length > 0) {
        const ok = await updateEmailAttributeValue(user.name, chainIds, [{ key: 'folder', value: "false" }], 'hostContract');
        if (!ok) notifyChainFailure('remove the selected emails from this folder');
      }
    } catch (err) {
      const errorStr = toErrorString(err || err.message);
      reportError({
        endpoint: '-',
        message: `FileName: MessageBoxHeader.js, Fun: handleMoveFolders, ${String(err.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      console.error(err);
    } finally {
      clearSelection();
      props.reRender();
      setloader(false);
      setText("")
      setFolderList(false)
    }
  }

  // Gated on what a bulk action can actually resolve, not on the raw length of the redux list. As
  // `filteredMessage.length > 0` this stayed true for ids that were no longer on screen, so the destructive
  // icons rendered as live controls over a page where every checkbox was empty.
  const hasSelection = selectionCount > 0;
  const canMoveToFolder = foldersValue.length > 0 && ["Inbox", "Insecure", "Sent"].includes(pageType);

  return (
    <>
      <div className="box-common-header box-header-type">
        <div className="page-type">
          <div className="page-type-check-box" id={selectedValue}>
            <Tooltip title={areAllVisibleSelected ? "Clear selection" : "Select everything on this page"} placement="top">
              <input
                type="checkbox"
                aria-label="Select all messages on this page"
                onChange={toggleSelectAllVisible}
                checked={areAllVisibleSelected}
                disabled={visibleIds.length === 0}
              />
            </Tooltip>
          </div>
          <div className="page-type">
            {/* onClick, not onChange. React only synthesises `change` for form elements, so on this
                div the handler could never fire and the whole menu was unreachable. */}
            <div
              className="page-type-arrow cursor-pointer"
              ref={checkboxRef}
              role="button"
              tabIndex={0}
              aria-label="Selection options"
              onClick={() => setIsCheckOpen((open) => !open)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsCheckOpen((open) => !open); } }}
            >
              {logo.down_arrow_box}
            </div>
            {type}
          </div>
          {/* The selected count was shown nowhere at all, so the size of a bulk action was invisible
              until after it had run. Inherits the header's own colour/theme; only the scale is toned
              down, which keeps this out of the stylesheet another engineer is in. */}
          {hasSelection && (
            <span className="selected-count" style={{ fontSize: "12px", fontWeight: 600 }} aria-live="polite">
              {selectionCount} selected
            </span>
          )}
          <AnchoredMenu open={isCheckOpen} anchorRef={checkboxRef} onClose={() => setIsCheckOpen(false)} width={200}>
            {SELECTION_MODES.map((mode) => (
              <button
                key={String(mode.value)}
                type="button"
                className={`anchored-menu-item ${selectedValue === mode.value && mode.value !== null ? 'is-active' : ''}`}
                onClick={() => applySelectionMode(mode.value)}
              >
                {mode.label}
                {selectedValue === mode.value && mode.value !== null && <span className="anchored-menu-check">&#10003;</span>}
              </button>
            ))}
          </AnchoredMenu>
          <div className="menu-settings">
            <div className="menu-dot-message-header cursor-pointer" ref={settingRef} onClick={() => setSettings((open) => !open)}>
              {logo.svg_menu_dot}
            </div>
            <AnchoredMenu open={settings} anchorRef={settingRef} onClose={() => setSettings(false)} width={220}>
              {props.pageType === "Sent" ? (
                <div className="anchored-menu-hint">No actions available in Sent.</div>
              ) : (
                <>
                  {!hasSelection && <div className="anchored-menu-hint">Select one or more emails to use these actions.</div>}
                  <button
                    type="button"
                    className="anchored-menu-item"
                    disabled={!hasSelection}
                    onClick={() => { handleReadMessage("read"); setSettings(false); }}
                  >
                    <MailOpen className="text-dark" /> <span>Mark as read</span>
                  </button>
                  <button
                    type="button"
                    className="anchored-menu-item"
                    disabled={!hasSelection}
                    onClick={() => { handleReadMessage("unread"); setSettings(false); }}
                  >
                    <Mail className="text-dark" /> <span>Mark as unread</span>
                  </button>
                </>
              )}
            </AnchoredMenu>
          </div>
        </div>
        <div className="menu-type-scroll flex items-center">
          {isVisibleFilter && (
            <div
              className={`show-checkbox-box-type ${isMobileView ? "dropdown-show-checkbox-box-type" : ""}`}
            >
              {isMobileView ? (
                <div
                  className="show-btn"
                  ref={dropdownRef}
                  onClick={() => setIsDropdownOpen((open) => !open)}
                >
                  {/* Reads the same `selectedOption` the desktop radios do, so crossing the breakpoint
                      can no longer leave the label disagreeing with the active filter. */}
                  <p>
                    {activeShowOption.label} <span>({activeShowOption.count})</span>
                  </p>
                  <svg
                    width="7"
                    height="6"
                    viewBox="0 0 7 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.25L3.5 3.75L6 1.25"
                      stroke="#A3ABAD"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              ) : (
                <>
                  <div className="show-btn show-text">
                    <span> Show : </span>
                  </div>
                  {showOptions.map((option, index) => (
                    <div className="show-btn" key={option.value}>
                      <label className="label-radio">
                        <input
                          type="radio"
                          className="input-radio on"
                          name="message-box"
                          value={option.value}
                          checked={selectedOption === option.value}
                          onChange={handleOptionChange}
                        />
                        <span className={selectedOption === option.value ? "active" : ""}>
                          {" "}
                          {option.label} <span>({option.count})</span>{" "}
                        </span>
                      </label>
                      {index < showOptions.length - 1 && <span className="pipe-symbol"> | </span>}
                    </div>
                  ))}
                </>
              )}
              {isMobileView && (
                <AnchoredMenu open={isDropdownOpen} anchorRef={dropdownRef} onClose={() => setIsDropdownOpen(false)} width={200}>
                  {showOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`anchored-menu-item ${selectedOption === option.value ? 'is-active' : ''}`}
                      onClick={handleDropdownSelect(option.value)}
                    >
                      {option.label} <span className="anchored-menu-count">{option.count}</span>
                    </button>
                  ))}
                </AnchoredMenu>
              )}
            </div>
          )}
          {hasSelection && props.pageType === "Folders" && (
            <div
              onClick={() => { folderMailDelete(); }}
              className="pe-10px cursor-pointer"
            >
              <Tooltip
                title="Delete"
                placement="top"
                enterDelay={500}
                leaveDelay={200}
              >
                <DeleteOutlined className="text-custom-gray" />
              </Tooltip>
            </div>
          )}
          {hasSelection &&
            props.pageType === "Trash" ? (
            hasSelection && (
              <div
                onClick={() => { permanentDelete(); }}
                className="pe-10px cursor-pointer"
              >
                <Tooltip title="Delete" placement="top">
                  <DeleteOutlined className="text-custom-gray" />
                </Tooltip>
              </div>
            )
          ) : (
            hasSelection &&
            props.pageType !== "Folders" && (
              <div
                onClick={() => { bulkDelete() }}
                className="pe-10px cursor-pointer"
              >
                <Tooltip title="Delete" placement="top">
                  <DeleteOutlined className="text-custom-gray" />
                </Tooltip>
              </div>
            )
          )}

          {hasSelection && pageType === "Archive" && (
            <div
              onClick={() => { bulkRestoreArchive(false) }}
              className="d-flex align-items-center justify-content-center cursor-pointer"
            >
              <Tooltip title="Move to inbox" placement="top">
                <ArchiveRestore
                  className="text-custom-gray"
                  width={18}
                  height={15}
                />
              </Tooltip>
            </div>
          )}
          {hasSelection &&
            (pageType === "Inbox" || pageType === "Insecure" || pageType === "Important") && (
              <div
                className="start-icon-div me-7px mb-2px cursor-pointer"
                onClick={() => { handlestared() }}
              >
                {" "}
                <Tooltip title="Starred" placement="top">
                  {logo.star_svg_outline}
                </Tooltip>
              </div>
            )}
          {hasSelection &&
            pageType !== "Trash" &&
            pageType !== "Archive" &&
            pageType !== "Folders" && (
              <div
                onClick={() => { bulkRestoreArchive(true) }}
                className="d-flex align-items-center justify-content-center cursor-pointer"
              >
                <Tooltip title="Archive" placement="top">
                  <Archive
                    className="text-custom-gray"
                    width={18}
                    height={15}
                  />
                </Tooltip>
              </div>
            )}


          {hasSelection && canMoveToFolder && (
            <div
              ref={folderTriggerRef}
              onClick={() => { setFolderList((open) => !open) }}
              className="d-flex align-items-center justify-content-center cursor-pointer ps-2"
            >
              <Tooltip title="Move to Folder" placement="top">
                <FolderInput
                  className="text-custom-gray"
                  width={18}
                  height={15}
                />
              </Tooltip>
            </div>
          )}
          {/* Portalled: as an absolutely-positioned child of `.menu-type-scroll` this list was clipped
              to the toolbar's height at <=768px, which is the "panel shows its title and nothing else"
              report. */}
          <AnchoredMenu open={folderList && hasSelection && canMoveToFolder} anchorRef={folderTriggerRef} onClose={() => setFolderList(false)} align="right" width={250}>
            {foldersValue.length === 0 ? (
              <div className="anchored-menu-hint">No folders yet. Create one from the sidebar.</div>
            ) : (
              foldersValue.map((data, index) => (
                <button type="button" key={index} className="anchored-menu-item" onClick={() => handleOptionClick(data)}>
                  {folders.folderIcon[`${getColorFromJson(data)}Folder`]}
                  {data.name}
                </button>
              ))
            )}
          </AnchoredMenu>

          {hasSelection && pageType === "Folders" && (
            <div
              onClick={() => { handleMoveFolders() }}
              className="d-flex align-items-center justify-content-center cursor-pointer"
            >
              <Tooltip title="Move to inbox" placement="top">
                <FolderOutput
                  className="text-custom-gray"
                  width={18}
                  height={15}
                />
              </Tooltip>
            </div>
          )}
          {hasSelection && pageType === "Trash" &&
            <Tooltip title="Restore" placement="top">
              <img src="/app/trash-restore-solid-svgrepo-com.svg" alt="trash" onClick={() => handleRestore()} className="mb-3px text-custom-gray cursor-pointer" />
            </Tooltip >
          }

          {isVisibleFilter && (
            <div className="sort-icon-action-01 d-flex align-items-center">
              {/* A real sort. `SORT_TYPE` existed in the reducer but was never dispatched and never
                  read, and this element's only handler was a commented-out line — so nothing on the
                  web client sorted anything. */}
              <div
                ref={sortRef}
                className={`sort-trigger ${isSortOpen ? 'is-open' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Sort messages"
                onClick={() => setIsSortOpen((open) => !open)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsSortOpen((open) => !open); } }}
              >
                <Tooltip title="Sort" placement="bottom">
                  <ArrowDownUp width={18} height={18} />
                </Tooltip>
              </div>
              <AnchoredMenu open={isSortOpen} anchorRef={sortRef} onClose={() => setIsSortOpen(false)} align="right" width={190}>
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`anchored-menu-item ${sortType === option.value ? 'is-active' : ''}`}
                    onClick={() => { dispatch({ type: "SORT_TYPE", payload: option.value }); setIsSortOpen(false); }}
                  >
                    {option.label}
                    {sortType === option.value && <span className="anchored-menu-check">&#10003;</span>}
                  </button>
                ))}
              </AnchoredMenu>

              <div className="pagination-controls">
                <span className="pagination-list-length text-truncate">
                  {" "}
                  {rangeStart} - {rangeEnd} of {totalLength}
                </span>
                <Tooltip title="Newer" placement="bottom">  <span
                  className="pagination-icon"
                  onClick={() => {
                    dispatch({
                      type: "PAGI_CLICK_LEFT",
                      payload: !pagiClickLeft,
                    });
                  }}
                >
                  {logo.left_arrow_svg}
                </span></Tooltip>
                <Tooltip title="older" placement="bottom">   <span
                  className="pagination-icon"
                  onClick={() => {
                    dispatch({
                      type: "PAGI_CLICK_RIGHT",
                      payload: !pagiClickRight,
                    });
                  }}
                >
                  {logo.right_arrow_svg}
                </span></Tooltip>
              </div>
            </div>
          )}
        </div>
      </div >
      <CompleteLoader isOpen={loader} text={text} />
    </>
  );
};

export default MessageBoxHeader;
